import React, {useEffect, useState} from 'react'
import appPath from '../lib/appPath'
import {routeToLogin} from '../lib/utils'
import {clearLocalSession} from '../auth/sessionSync'

// A crash here usually means the app rendered against inconsistent auth state
// (the classic one: a stale profile role sending /admin-panel and /login
// bouncing at each other until React gives up with "Maximum update depth
// exceeded"). Rather than parking the user on a dead end that only a manual
// refresh clears, bounce to /login — it re-derives the real state from the
// token and forwards to the right landing page, or starts a fresh signin.
//
// Once per tab only. If we crash again after having already recovered, the
// redirect is not fixing it, so stop and offer the bigger hammer instead of
// spinning between the crash and the login page.
const RECOVERY_FLAG_KEY = 'combird_error_recovered';
const RECOVERY_DELAY_MS = 1200;

const alreadyRecovered = () => {
  try {
    return sessionStorage.getItem(RECOVERY_FLAG_KEY) === '1';
  } catch {
    return true; // no storage → don't auto-redirect, we can't rate-limit it
  }
};

const ErrorFallback = ({error}) => {
  const [recovering] = useState(() => !alreadyRecovered());

  useEffect(() => {
    if (!recovering) return undefined;
    try {
      sessionStorage.setItem(RECOVERY_FLAG_KEY, '1');
    } catch { /* best effort */ }
    const timer = setTimeout(() => {
      window.location.replace(appPath('/login'));
    }, RECOVERY_DELAY_MS);
    return () => clearTimeout(timer);
  }, [recovering]);

  // Full reset: drop the session entirely so /login starts from nothing.
  const startOver = () => {
    clearLocalSession();
    routeToLogin();
  };

  return (
    <>
      <div role="alert" style={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16}}>
        <p>Something went wrong: </p>
        <pre style={{color: "red"}}>{error.message}</pre>
        {recovering ? (
          <p>Taking you back to the login page…</p>
        ) : (
          <>
            <p>Please sign in again.</p>
            <button
              type="button"
              onClick={startOver}
              style={{padding: '10px 24px', fontSize: 16, cursor: 'pointer'}}
            >
              Go to login
            </button>
          </>
        )}
      </div>
    </>
  );
}

export default ErrorFallback
